import { ConnectionStatus, MeetingParticipantType } from "database";

export {
  connectionNotificationPeerIds,
  directoryEntryMatchesPeers,
} from "./notification-party.pure";

export const LIVE_CONNECTION_STATUS_EVENT = "sajilni:live-connection-status";

export function emitLiveConnectionStatus(
  peerIds: Array<string | null | undefined>,
  status: ConnectionStatus | null,
  pair?: { senderId?: string | null; receiverId?: string | null }
) {
  if (typeof window === "undefined") return;
  const ids = [...new Set(peerIds.filter((id): id is string => Boolean(id)))];
  if (ids.length === 0) return;
  window.dispatchEvent(
    new CustomEvent(LIVE_CONNECTION_STATUS_EVENT, {
      detail: {
        peerIds: ids,
        status,
        senderId: pair?.senderId,
        receiverId: pair?.receiverId,
      },
    })
  );
}

export const firstNonEmpty = (...vals: (string | null | undefined)[]) => {
  for (const v of vals) {
    if (typeof v === "string" && v.trim()) return v.trim();
  }
  return "";
};

export const displayPersonName = (person?: {
  name?: string | null;
  email?: string | null;
  image?: string | null;
  profiles?: { name?: string | null; image?: string | null }[] | null;
} | null) => {
  if (!person) return { name: "", image: null as string | null };
  const profile = person.profiles?.[0];
  return {
    // Same priority as participants list/card: profile → user → email
    name: firstNonEmpty(profile?.name, person.name, person.email),
    image: firstNonEmpty(profile?.image, person.image) || null,
  };
};

export const displayCompanyName = (company?: {
  translations?: { name?: string | null }[] | null;
  boothName?: string | null;
  name?: string | null;
  email?: string | null;
  logo?: string | null;
} | null) => {
  if (!company) return { name: "", image: null as string | null };
  return {
    name: firstNonEmpty(
      company.translations?.[0]?.name,
      company.boothName,
      company.name,
      company.email
    ),
    image: company.logo || null,
  };
};

/**
 * True when this row is the invitee's original request notification mutated to
 * APPROVED/REJECTED (roles still match the connection/meeting). False for the
 * flipped "X accepted your request" alert sent to the original requester.
 */
export const isInviteeSideConfirmation = (notification: {
  senderId?: string | null;
  receiverId?: string | null;
  connectionRequest?: { senderId?: string | null; receiverId?: string | null } | null;
  meeting?: {
    participants?: { entityId?: string | null; type?: string }[] | null;
  } | null;
}) => {
  const cr = notification.connectionRequest;
  if (cr?.senderId && cr?.receiverId) {
    return (
      notification.senderId === cr.senderId &&
      notification.receiverId === cr.receiverId
    );
  }
  const participants = notification.meeting?.participants;
  if (!participants?.length) return false;
  const reqSender = participants.find(
    (p) => p.type === MeetingParticipantType.SENDER
  )?.entityId;
  const reqReceiver = participants.find(
    (p) => p.type === MeetingParticipantType.RECEIVER
  )?.entityId;
  return (
    Boolean(reqSender && reqReceiver) &&
    notification.senderId === reqSender &&
    notification.receiverId === reqReceiver
  );
};
